import React from 'react';
import { Loader2, Save } from 'lucide-react';
import { toast } from 'react-toastify';
import { StripeAdminSettings, updateStripeSettings } from '../../../api/adminSettingsApi';

interface StripeConfigurationProps {
  initialValues: StripeAdminSettings | null;
  loading: boolean;
  onRefresh: () => void;
  onRequestSecretUpdate: (title: string, fieldLabel: string) => void;
}

export const StripeConfiguration: React.FC<StripeConfigurationProps> = ({ initialValues, loading, onRefresh, onRequestSecretUpdate }) => {
  const [formData, setFormData] = React.useState<StripeAdminSettings>({ publishableKey: '', clientId: '', secretKey: '', webhookSecret: '' });
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (initialValues) {
      setFormData({
        publishableKey: initialValues.publishableKey || '',
        clientId: initialValues.clientId || '',
        secretKey: initialValues.secretKey || '',
        webhookSecret: initialValues.webhookSecret || '',
      });
    }
  }, [initialValues]);

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    try {
      await updateStripeSettings(formData);
      toast.success('Stripe configuration updated');
      onRefresh();
    } catch {
      toast.error('Failed to update configuration');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center rounded-2xl border border-strivre-lightnavy bg-slate-50/40 p-8 text-slate-500">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
        Loading Stripe settings...
      </div>
    );
  }

  return (
    <form onSubmit={handleSave} className="space-y-5 rounded-2xl border border-strivre-lightnavy bg-white p-6 shadow-sm text-left">
      <div className="space-y-2">
        <label className="text-sm font-semibold text-slate-700">Publishable Key</label>
        <input
          value={formData.publishableKey}
          onChange={(e) => setFormData({ ...formData, publishableKey: e.target.value })}
          className="w-full rounded-xl border border-strivre-lightnavy px-4 py-2.5 outline-none text-slate-900 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
        />
      </div>

      <div className="space-y-2">
        <label className="text-sm font-semibold text-slate-700">Client ID</label>
        <input
          value={formData.clientId}
          onChange={(e) => setFormData({ ...formData, clientId: e.target.value })}
          className="w-full rounded-xl border border-strivre-lightnavy px-4 py-2.5 outline-none text-slate-900 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-1 gap-4 rounded-2xl border border-strivre-lightnavy bg-slate-50/40 p-5">
        <div className="space-y-2">
          <label className="text-sm font-semibold text-slate-700">Secret Key</label>
          <div className="flex items-center gap-2">
            <div className="flex-1 rounded-xl border border-strivre-lightnavy bg-white px-4 py-2.5 text-sm text-slate-600">
              {initialValues?.secretKey ? 'Configured ✅' : 'Not Configured ❌'}
            </div>
            {/* <button type="button" className="rounded-xl border border-strivre-lightnavy px-3 py-2 text-sm font-semibold text-slate-600">
              {showSecret ? <EyeOff size={16} /> : <Eye size={16} />} 
            </button> */}
          </div>
        </div>

        {/* <div className="space-y-2">
          <label className="text-sm font-semibold text-slate-700">Webhook Secret</label>
          <div className="flex items-center gap-2">
            <div className="flex-1 rounded-xl border border-strivre-lightnavy bg-white px-4 py-2.5 text-sm text-slate-600">
              {initialValues?.webhookSecret ? 'Configured ✅' : 'Not Configured ❌'}
            </div>
            <button type="button" className="rounded-xl border border-strivre-lightnavy px-3 py-2 text-sm font-semibold text-slate-600">
              {showWebhook ? <EyeOff size={16} /> : <Eye size={16} />} 
            </button>
          </div>
        </div> */}
      </div>

      <div className="flex flex-wrap gap-3">
        <button type="button" onClick={() => onRequestSecretUpdate('Update Secret Key', 'Secret Key')} className="rounded-xl border border-strivre-lightnavy bg-white px-4 py-2.5 text-sm font-semibold text-slate-700">Update Secret Key</button>
        <button type="button" onClick={() => onRequestSecretUpdate('Update Webhook Secret', 'Webhook Secret')} className="rounded-xl border border-strivre-lightnavy bg-white px-4 py-2.5 text-sm font-semibold text-slate-700">Update Webhook Secret</button>
      </div>

      <div className="flex justify-end">
        <button type="submit" disabled={saving} className="flex items-center gap-2 rounded-2xl bg-strivre-burgundy px-6 py-3 text-sm font-semibold text-white shadow-sm">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save size={16} />}
          {saving ? 'Saving...' : 'Save Configuration'}
        </button>
      </div>
    </form>
  );
};
